import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import LivingNetwork from "@/components/LivingNetwork";
import TopoBackground from "@/components/TopoBackground";
import { PARTNERS } from "@/data/content";

const LAND_CHAIN = [
  "Country", "Communities", "Stories", "Language", "Species",
  "Fire", "Water", "Culture", "Stewardship", "Future Generations",
];

export default function Network() {
  return (
    <div data-testid="network-page" className="pt-20">
      <section className="relative py-28 md:py-36 overflow-hidden">
        <TopoBackground opacity={0.06} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label className="block mb-6">The Living Network</Label>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED]">
              Not databases connected together. Landscapes connected together.
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              Each community remains sovereign. Knowledge stays connected to place.
              Communities choose what they share. Knowledge moves through trust — not
              extraction.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Network visual */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-2xl border border-white/8 bg-[#080C0A] p-6 md:p-12">
              <LivingNetwork />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Knowledge flows through landscape */}
      <section className="py-20 md:py-28 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4 text-center">Knowledge flows through landscape</Label>
            <p className="text-center font-display font-light text-2xl md:text-3xl text-[#F4F1ED] max-w-2xl mx-auto">
              Knowledge stays connected to place as it moves between us.
            </p>
          </Reveal>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-3 gap-y-4">
            {LAND_CHAIN.map((node, i) => (
              <Reveal key={node} delay={i * 0.05}>
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-[#0C110E] hover:border-[#D4A373]/50 transition-colors duration-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D4A373]" />
                    <span className="font-display text-lg md:text-xl text-[#F4F1ED]">{node}</span>
                  </span>
                  {i < LAND_CHAIN.length - 1 && (
                    <span className="text-[#D4A373]/50">→</span>
                  )}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Principles of the network */}
      <section className="py-20 md:py-28 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-10">
          {[
            { t: "Sovereignty", d: "Each community holds authority over its own knowledge, always." },
            { t: "Consent", d: "Communities choose what they share, and with whom, at every step." },
            { t: "Trust, not extraction", d: "Knowledge moves through relationships, never harvested from them." },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 0.08}>
              <div className="p-8 rounded-lg border border-white/8">
                <p className="wk-label mb-4">0{i + 1}</p>
                <h3 className="font-display text-2xl text-[#F4F1ED]">{x.t}</h3>
                <p className="mt-3 text-[#A8A5A1] leading-relaxed">{x.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Who we work with */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Who We Work With</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
              A network of keepers, holders and carers.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-x-12">
            {PARTNERS.map((p, i) => (
              <Reveal key={p} delay={i * 0.03}>
                <div
                  data-testid={`partner-type-${i}`}
                  className="py-4 border-b border-white/10 text-lg text-[#D8D4CE]"
                >
                  {p}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/partner"
              data-testid="network-cta-link"
              className="mt-12 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              Join the network <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
